import { useEffect, useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            お問い合わせ
          </h2>
          <p className="text-lg text-teal-50">
            システム開発のご相談・お見積もりはお気軽にどうぞ
          </p>
        </div>
      </section>

      {/* Development Flow Section */}
      <section id="flow" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">システム開発ご依頼の流れ</h3>
          </div>

          <div className="space-y-8">
            {[
              {
                step: '01',
                title: 'システム開発内容の相談',
                description: 'お客様のIT周りの課題をヒアリングさせていただきます。'
              },
              {
                step: '02',
                title: '課題内容の提案・見積もり',
                description: 'お客様の課題に対して適切なITソリューションの提案・見積もりを行います。ここまでは無料で対応させていただきます。'
              },
              {
                step: '03',
                title: '契約',
                description: '提案・見積もり内容をもとに要件を決めて、それに応じた契約を行います。いずれも作成する費用はシステムによって変わりますので、お気軽にご連絡ください。'
              },
              {
                step: '04',
                title: '開発・納品・導入',
                description: '要件にそったシステムを開発・納品・導入を行います。'
              },
              {
                step: '05',
                title: '保守運用',
                description: '保守運用の契約をしている場合は、導入後のサポートも行います。'
              }
            ].map((item, index) => (
              <div key={index} className="flex gap-6 items-start bg-white p-6 rounded-xl shadow-sm">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1 pt-2">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせフォーム</h3>
            <p className="text-lg text-gray-600">
              お気軽にお問い合わせください。担当者より折り返しご連絡いたします。
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl shadow-lg p-8 md:p-12">
            <form id="contact-form" data-readdy-form action="https://readdy.ai/api/form/d5do2ufdcu97hjrn4980" method="POST" onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const subject = formData.get('subject') as string;
              const message = formData.get('message') as string;

              if (!name || !email || !subject) {
                alert('必須項目を入力してください。');
                return;
              }

              if (message && message.length > 500) {
                alert('メッセージは500文字以内で入力してください。');
                return;
              }

              const submitData = new URLSearchParams();
              submitData.append('name', name);
              submitData.append('email', email);
              submitData.append('subject', subject);
              if (message) submitData.append('message', message);

              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: submitData.toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('お問い合わせを送信しました。ありがとうございます。');
                  form.reset();
                } else {
                  alert('送信に失敗しました。もう一度お試しください。');
                }
              })
              .catch(() => {
                alert('送信に失敗しました。もう一度お試しください。');
              });
            }}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    氏名 <span className="text-red-500">*</span>
                  </label>
                  <input type="text" id="name" name="name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    題名 <span className="text-red-500">*</span>
                  </label>
                  <input type="text" id="subject" name="subject" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    メッセージ本文（任意）
                  </label>
                  <textarea id="message" name="message" rows={6} maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"></textarea>
                  <p className="text-xs text-gray-500 mt-1">500文字以内で入力してください</p>
                </div>

                <button type="submit" className="w-full bg-teal-500 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  送信する
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to Top Button */}
      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`fixed bottom-8 right-8 w-12 h-12 bg-teal-500 text-white rounded-full shadow-lg hover:bg-teal-600 transition-all flex items-center justify-center cursor-pointer ${isScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-label="ページトップへ">
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
